import React, { useState } from "react";
import "./style.scss";
import CloseButton from "react-bootstrap/CloseButton";
import { Document, Page, pdfjs } from "react-pdf";
import Loader from "../../Loader";
import { getFieldConfig, getFieldSettings } from "../utils/fieldConfig";
import PreviewFieldRenderer from "../FieldRenderer/PreviewFieldRenderer";

// Set up the PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const PreviewModal = ({ show, onHide, documentData, designUrl, placedFields = [], modalSettingsObj, scale = 1 }) => {
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [pdfReady, setPdfReady] = useState(false);
  
  // Reset loading state when opening modal with new document
  React.useEffect(() => {
    if (show && designUrl) {
      setIsPdfLoading(true);
      setPdfReady(false);
    }
  }, [show, designUrl]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setIsPdfLoading(false);
    setNumPages(numPages);
    // Give PDF time to render before marking as ready
    setTimeout(() => setPdfReady(true), 200);
  };

  const onDocumentLoadError = (error) => {
    console.error("Error loading PDF:", error);
    setIsPdfLoading(false);
  };

  const constrainFieldPosition = (field, pageIndex, scale) => {
    // Always use 1.0 scale for field positioning since PDF is rendered at 1.0 scale
    const baseScale = 1.0;
    
    if (!pdfReady) {
      return {
        x: field.x * baseScale,
        y: field.y * baseScale,
        width: field.width * baseScale,
        height: (field.height || 30) * baseScale
      };
    }

    // Get PDF page container element
    const pageContainer = document.querySelector(`[data-page-number="${pageIndex + 1}"]`);
    
    // Try multiple selectors to find the PDF page element
    let pdfPageElement = pageContainer?.querySelector('.react-pdf__Page');
    if (!pdfPageElement) {
      pdfPageElement = pageContainer?.querySelector('.react-pdf__Page__canvas');
    }
    if (!pdfPageElement) {
      pdfPageElement = pageContainer?.querySelector('canvas');
    }
    
    if (!pdfPageElement) {
      return {
        x: field.x * baseScale,
        y: field.y * baseScale,
        width: field.width * baseScale,
        height: (field.height || 30) * baseScale
      };
    }

    const pageRect = pdfPageElement.getBoundingClientRect();
    const containerRect = pageContainer.getBoundingClientRect();
    
    // Calculate PDF offset within container (since PDF might be centered)
    const pdfOffsetX = pageRect.left - containerRect.left;
    const pdfOffsetY = pageRect.top - containerRect.top;
    
    // Field dimensions using base scale (1.0) to match PDF rendering
    const fieldWidth = field.width * baseScale;
    const fieldHeight = (field.height || 30) * baseScale;
    
    // Calculate constrained position relative to PDF page using base scale
    let constrainedX = field.x * baseScale;
    let constrainedY = field.y * baseScale;
    
    // Ensure field stays within PDF page boundaries
    if (pageRect.width > 0 && constrainedX + fieldWidth > pageRect.width) {
      constrainedX = Math.max(0, pageRect.width - fieldWidth);
    }
    if (pageRect.height > 0 && constrainedY + fieldHeight > pageRect.height) {
      constrainedY = Math.max(0, pageRect.height - fieldHeight);
    }
    
    // Final position includes PDF offset within container
    const finalX = constrainedX + pdfOffsetX;
    const finalY = constrainedY + pdfOffsetY;

    return {
      x: finalX,
      y: finalY,
      width: fieldWidth,
      height: fieldHeight
    };
  };
  
  if (!show) return null;

  return (
    <div className="preview-modal-overlay">
      <div className="preview-modal">
        <div className="preview-modal-header">
          <h2>Preview</h2>
          <div className="close-button-wrapper">
            <CloseButton onClick={onHide} className="close-cta-wrapper" />
          </div>
        </div>
        <div className="preview-modal-content">
          {documentData && designUrl ? (
            <div className="document-preview">
              <div className="pdf-preview-container">
                <Document
                  file={designUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={<Loader />}
                  // error={<div>Failed to load PDF</div>}
                >
                  {Array.from(new Array(numPages), (el, index) => (
                    <div key={`page_${index + 1}`} className="pdf-page-container" data-page-number={index + 1}>
                      <Page
                        pageNumber={index + 1}
                        scale={1.0}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        className="pdf-page"
                      />
                      {!isPdfLoading && pdfReady && placedFields
                        .filter((field) => field.pageNumber === index + 1)
                        .map((field) => {
                          const fieldConfig = getFieldConfig(field);
                          const dimensions = fieldConfig?.dimensions || "275px";
                          const width = field.width || (dimensions.includes("px")
                            ? parseInt(dimensions)
                            : parseInt(dimensions.split("x")[0]));

                          // Get field-specific settings for font styling
                          const fieldSettings = getFieldSettings(
                            field,
                            modalSettingsObj,
                          );
                          const fontStyles = {
                            fontSize: field.fontSize
                              ? `${field.fontSize}px`
                              : fieldSettings.fontSize
                              ? `${fieldSettings.fontSize}px`
                              : undefined,
                            fontFamily: field.fontStyle || fieldSettings.fontStyle || undefined,
                          };

                          // Get constrained position and dimensions
                          const constrainedField = constrainFieldPosition({ ...field, width }, index, scale);

                          return (
                            <div
                              key={field.id}
                              className="placed-field"
                              data-field-id={field.id}
                              style={{
                                position: "absolute",
                                left: `${constrainedField.x}px`,
                                top: `${constrainedField.y}px`,
                                width: `${constrainedField.width}px`,
                                height: `${constrainedField.height}px`,
                              }}
                            >
                              <div className="field-wrapper">
                                <PreviewFieldRenderer 
                                  field={field}
                                  fontStyles={fontStyles}
                                  modalSettingsObj={modalSettingsObj}
                                />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ))}
                </Document>
              </div>
            </div>
          ) : (
            <div className="no-preview">
              <p>No preview available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
